import React, { useEffect, useState, useContext } from "react";
import * as _ from "lodash";

import "./EUMRVVoyages.scss";

import CustomTable from "../../../../../../common/customTable/CustomTable";
import {
	EUMRV_VOYAGES_HEADERS,
	EuVoyagesEmissionsAfterDerogationColumns,
} from "../../../../../../constants/CustomTableHeaders";
import { useSelector } from "react-redux";
import { ClaimsContext } from "../../../../../../services/ClaimsService";
import { ClaimType } from "../../../../../../constants/claims";
import { FeatureType } from "../../../../../../constants/FeatureToggles";

const EuVoyagesEmissionAfterDerogation = ({ data, loading }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const [columnData, setColumndata] = useState(
		EuVoyagesEmissionsAfterDerogationColumns
	);
	
    	const mrvAccessorKeys = [
		"co2Mrv",
		"ch4Mrv",
		"n2oMrv",
		"co2EqMrv",
	];

	const user = useSelector((state) => state.userInfo.userData);
	const columnsSource = 
		 EuVoyagesEmissionsAfterDerogationColumns.filter(
			col => !mrvAccessorKeys.includes(col.accessorKey)
		)
		
	const alwaysColumns = columnsSource?.filter(
		(column) => !column.condition
	);
	const conditionalColumns = columnsSource?.filter(
		(column) => column.condition && userHasClaim(column.condition)
	);

	const staticColumns = userHasClaim(ClaimType.VIEW_ABS_INTERNAL_COLUMNS)
		? [
			...alwaysColumns.slice(0, 8),
			...(conditionalColumns?.filter(
				(item) => item.accessorKey === "calcTimeAtSea"
			) || []),
			...alwaysColumns.slice(8, 10),
			...conditionalColumns?.filter(
				(item) => item.accessorKey === "calcDistanceTravelled"
			),
			...alwaysColumns.slice(10),
		]
		: [
			...alwaysColumns.slice(0, 8),
			...alwaysColumns.slice(9, 11),
			...alwaysColumns.slice(12),
		];

	// Refine the data by adding a "DieselGasOilCons" entry if not present
	useEffect(() => {
		if (
			data &&
			data[0]?.voyageConsumptions.some((fuel) =>
				["MDO", "MGO"].includes(fuel.fuelName)
			)
		) {
			data?.forEach((entry) => {
				if (
					entry?.voyageConsumptions.every(
						(fuel) => fuel.fuelName !== "DieselGasOilCons"
					)
				) {
					entry.voyageConsumptions.unshift({
						fuelName: "DieselGasOilCons",
						consumption: entry.dieselGasOilCons,
					});
				}
			});
		}
	}, [data]);

	// method to filter dynamic columns based on non-zero values
	const getFilteredColumns = (data, columnType, columns) => {
		const nonZeroNames = new Set();

		// Collect non-zero fuel names based on the type (consumption, MNC, transport work)
		data?.forEach((item) => {
			item[columnType]?.forEach((entry) => {
				if (
					columnType === "voyageConsumptions" &&
					entry.consumption !== 0 &&
					entry.consumption !== null &&
					entry.consumption !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
				if (
					columnType === "voyageMncs" &&
					entry.mnc !== 0 &&
					entry.mnc !== null &&
					entry.mnc !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
				if (
					columnType === "transportWorks" &&
					entry.value !== 0 &&
					entry.value !== null &&
					entry.value !== undefined
				) {
					nonZeroNames.add(entry?.transportWorkType);
				}
			});
		});

		// Filter columns based on non-zero values
		return columns.filter((column) => {
			const name = column.accessorKey.split("_")[1];
			return nonZeroNames.has(name);
		});
	};

	// method to filter static columns based on non-zero values
	const getFilteredStaticColumns = (objArray, columnsArr) => {
		const nonZeroHeaders = new Set();
		objArray?.forEach((obj) => {
			Object?.entries(obj)?.forEach(([key, value]) => {
				if (value !== 0 && value !== null && value !== undefined) {
					nonZeroHeaders.add(key);
				}
			});
		});
		return columnsArr.filter((column) =>
			nonZeroHeaders.has(column.accessorKey)
		);
	};

	const GetPossibleKeys = (allData, dataType, keyName) => {
		return _.chain(allData).flatMap(dataType).map(keyName).uniq().value();
	};

	// Initialize dynamic columns for consumption, MNC, and transport work
	const getDynamicColumns = (data) => {
		const dynamicColumns = [];
		const dynamicMncColumns = [];
		const dynamicTransportWork = [];

		if (Array.isArray(data[0]?.voyageConsumptions)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"voyageConsumptions",
				"fuelName"
			);
			possibleKeys?.forEach((item) => {
				dynamicColumns.push({
					id: `voyageConsumptions_${item}`,
					accessorKey: `voyageConsumptions_${item}`,
					header: `${item === "DieselGasOilCons"
						? "Diesel/Gas Oil Cons. (MT)"
						: `${item} Cons. (MT)`
						}`,
					Cell: ({ row }) =>
						row.original.voyageConsumptions.find(
							(fuel) => fuel.fuelName === item
						)?.consumption || 0,
				});
			});
		}

		if (Array.isArray(data[0]?.voyageMncs)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"voyageMncs",
				"fuelName"
			);
			possibleKeys?.forEach((item) => {
				dynamicMncColumns.push({
					id: `voyageMncs_${item}`,
					accessorKey: `voyageMncs_${item}`,
					header: `${item} MNC. (MT)`,
					Cell: ({ row }) =>
						row.original.voyageMncs.find(
							(fuel) => fuel.fuelName === item
						)?.mnc || 0,
				});
			});
		}

		if (Array.isArray(data[0]?.transportWorks)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"transportWorks",
				"transportWorkType"
			);
			possibleKeys?.forEach((item) => {
				dynamicTransportWork.push({
					id: `transportWorks_${item}`,
					accessorKey: `transportWorks_${item}`,
					header: `Transport Work (${item}*nm)`,
					Cell: ({ row }) =>
						row.original.transportWorks.find(
							(work) => work.transportWorkType === item
						)?.value || 0,
				});
			});
		}

		// Return filtered dynamic columns based on data content
		return {
			dynamicColumns: getFilteredColumns(
				data,
				"voyageConsumptions",
				dynamicColumns
			),
			dynamicMncColumns: getFilteredColumns(
				data,
				"voyageMncs",
				dynamicMncColumns
			),
			dynamicTransportWork: getFilteredColumns(
				data,
				"transportWorks",
				dynamicTransportWork
			),
		};
	};

	// Use useEffect to update column data only when the data or loading changes
	useEffect(() => {
		if (loading) {
			setColumndata(staticColumns);
		} else {
			setColumndata([]);
			if (data && data?.length > 0) {
				const {
					dynamicColumns,
					dynamicMncColumns,
					dynamicTransportWork,
				} = getDynamicColumns(data);
				const dynamicEUMRVVoyageColumnsData =
					dynamicColumns || dynamicMncColumns || dynamicTransportWork
						? userHasClaim(ClaimType.VIEW_ABS_INTERNAL_COLUMNS)
							? [
								...staticColumns.slice(0, 1),
								...EuVoyagesEmissionsAfterDerogationColumns?.slice(1, 4),
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										4,
										19
									)
								),
								...dynamicColumns,
								...dynamicMncColumns,
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										19,
										24
									)
								),
								//feature toggle for blend fuel registry
								...(
									 getFilteredStaticColumns(
										data,
										columnsSource?.slice(24, 28)
									)
									),
								...dynamicTransportWork,
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										28
									)
								),
							]
							: [
								...staticColumns.slice(0, 1),
								...EuVoyagesEmissionsAfterDerogationColumns?.slice(1, 4),
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										4,
										8
									)
								),
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										9,
										11
									)
								),
								...getFilteredStaticColumns(
									data,
									columnsSource?.slice(
										12,
										19
									)
								),
								...dynamicColumns,
								...dynamicMncColumns,
								...getFilteredStaticColumns(
                                    data,
									columnsSource?.slice(
										19,
										24
									)
								),
								//feature toggle for blend fuel registry
								...(
									 getFilteredStaticColumns(
										data,
										columnsSource?.slice(24, 28)
									)
									),
								...dynamicTransportWork,
								...getFilteredStaticColumns(
                                    data,
									columnsSource?.slice(
										28
									)
								),
							]
						: [
							...EuVoyagesEmissionsAfterDerogationColumns?.slice(0, 4),
							...getFilteredStaticColumns(data, staticColumns.slice(4))];
				setColumndata(dynamicEUMRVVoyageColumnsData);
			} else {
				setColumndata(staticColumns);
			}
		}
	}, [data, loading]);

	return (
		<div className="EUMRV-custom-table-container mt-3 emission-reported-voyage eu_mrv_voyage">
			<CustomTable
				columnsData={columnData}
				renderTableData={data || []}
				customProps={{
					isLoading: loading,
					header: EUMRV_VOYAGES_HEADERS.EMISSION_DEROGATION,
					enableSort: false,
					tableName: "emissions-report",
					maxHeight: "30vh",
					columnOrder: columnData.map((column) => column.accessorKey),
				}}
			/>
		</div>
	);
};

export default EuVoyagesEmissionAfterDerogation;
